import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ExitPromptType } from '@frame/ui-components/exit-prompt'
import { Footer } from '@frame/ui-components/footer'
import { LoadingSpinner } from '@frame/ui-components/loading-spinner'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { Body } from '@frame/ui-primitives/body'
import { Button } from '@frame/ui-primitives/button'
import { H2 } from '@frame/ui-primitives/h2'

import { PayrollProviderInfo, PlatformInfoType } from '@frame/components/screens/non-terminal/payroll-provider-info'
import { useRootContext } from '@frame/lib/context'
import { useAutoNavigate } from '@frame/lib/hooks/useAutoNavigate'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { useEmployerTitle } from '@frame/lib/hooks/useEmployerTitle'
import { useHeaderTitle } from '@frame/lib/hooks/useHeaderTitle'
import { useShowExitPrompt } from '@frame/lib/hooks/useShowExitPrompt'
import { useStrategies } from '@frame/lib/hooks/useStrategies'
import { useVerifyViaDocuments } from '@frame/lib/hooks/useVerifyViaDocuments'
import { useStore } from '@frame/lib/stores/useStore'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

export function CheckPlatform() {
  const location = useLocation()
  const showExitPrompt = useShowExitPrompt()
  const navigate = useNavigate()
  const task = useDirectTask()
  const { actions } = useRootContext()
  const { mutateAndNavigate } = useAutoNavigate()
  const { getEmployerTitle } = useEmployerTitle()
  const { documentsEnabled } = useStrategies()
  const verifyViaDocuments = useVerifyViaDocuments()
  const title = useHeaderTitle()
  const [isLoading, setIsLoading] = useState(false)

  const { docsForMortgageEnabled } = useStore(({ session }) => ({
    docsForMortgageEnabled: session?.configuration.docs_for_mortgage_enabled,
  }))

  const params = new URLSearchParams(location.search)
  const paramEmployerName = params.get('employerName') || ''
  const employerName = task?.employer_name || paramEmployerName

  const goToPlatformSearch = () => {
    const employerNameParams = new URLSearchParams()
    if (task?.is_external_employment) {
      employerNameParams.set('employerName', employerName)
    } else {
      employerNameParams.set('employerName', paramEmployerName)
    }
    if (task?.id) {
      navigate(`${routes.platformSearch}/${task.id}?${employerNameParams.toString()}`)
    } else {
      navigate(`${routes.platformSearch}?${employerNameParams.toString()}`)
    }
  }

  const onExit = async () => {
    if (documentsEnabled || docsForMortgageEnabled) {
      setIsLoading(true)
      verifyViaDocuments(task)
    } else if (task?.is_external_employment) {
      await actions.skipDirectTask(task.id)
      mutateAndNavigate()
    } else {
      const withSearchQuery = new URLSearchParams()
      withSearchQuery.set('q', paramEmployerName)
      const confirmEmployerPath = task?.id ? `${routes.confirmEmployer}/${task.id}` : routes.confirmEmployer
      navigate(`${confirmEmployerPath}?${withSearchQuery.toString()}`)
    }
  }

  return (
    <>
      <WidgetHeaderWithNavigation
        title={title}
        showExitPrompt={showExitPrompt}
        exitPromptType={ExitPromptType.EMPLOYER}
      />
      <Body data-cy={DataAttribute.POS_CHECK_PLATFORM} className="h-full p-5" paddingTopOverride={8}>
        <H2 className="mb-4 text-left">
          Do you know who handles paychecks for {getEmployerTitle({ possessive: true })}?
        </H2>
        <PayrollProviderInfo type={PlatformInfoType.PLATFORM_CHECK} />
      </Body>
      <Footer
        buttons={{
          primary: (
            <Button data-cy={DataAttribute.KNOWN_PAYROLL} onClick={goToPlatformSearch} intent="primary" applyCobranding>
              Yes, I know the payroll provider
            </Button>
          ),
          secondary: (
            <Button
              data-cy={DataAttribute.UNKNOWN_PAYROLL}
              disabled={isLoading}
              onClick={onExit}
              intent="secondary"
              Icon={isLoading ? LoadingSpinner : undefined}
            >
              No
            </Button>
          ),
        }}
      />
    </>
  )
}
